import { useState } from "react";
import { KeyRound, Info } from "lucide-react";
import BaseModal from "../BaseComponents/BaseModal/BaseModal";
import { useToast } from "../../context/ToastContext";
import { useCreatePermission } from "../../api/users/usePermissionsApi";
import { MODULE_ORDER } from "../../constants/permissionMeta";
import styles from "./AddPermissionModal.module.css";

const ACTIONS = ["view", "create", "update", "delete", "assign"];

const empty = { module: "", resource: "", action: "view", description: "" };

const slug = (v) => v.toLowerCase().replace(/[^a-z0-9_]/g, "");

const AddPermissionModal = ({ open, onClose, onCreated }) => {
  const { showToast } = useToast();
  const { execute: createPermission, loading } = useCreatePermission();
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");

  const setField = (k, v) => {
    setError("");
    setForm((f) => ({ ...f, [k]: v }));
  };

  const previewKey =
    form.module && form.resource && form.action
      ? `${form.module}.${form.resource}.${form.action}`
      : "module.resource.action";

  const handleClose = () => {
    setForm(empty);
    setError("");
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!form.module.trim() || !form.resource.trim() || !form.action) {
      setError("Module, resource and action are required");
      return;
    }
    try {
      await createPermission({
        module: form.module.trim(),
        resource: form.resource.trim(),
        action: form.action,
        description: form.description.trim(),
        isSystem: false,
      });
      showToast("Permission created successfully", "success");
      handleClose();
      onCreated?.();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to create permission";
      setError(msg);
      showToast(msg, "error");
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title="Add Permission"
      width="520px"
      footer={
        <>
          <button className={styles.cancelBtn} onClick={handleClose} disabled={loading}>
            Cancel
          </button>
          <button className={styles.saveBtn} onClick={handleSubmit} disabled={loading}>
            {loading ? "Creating…" : "Create Permission"}
          </button>
        </>
      }
    >
      <div className={styles.form}>
        {error && (
          <div className={styles.errorBanner}>
            <Info size={15} />
            {error}
          </div>
        )}

        <div className={styles.row}>
          <div className={styles.field}>
            <label className={styles.label}>
              Module <span className={styles.req}>*</span>
            </label>
            <input
              className={styles.input}
              list="module-options"
              value={form.module}
              onChange={(e) => setField("module", slug(e.target.value))}
              placeholder="finance"
            />
            <datalist id="module-options">
              {MODULE_ORDER.map((m) => (
                <option key={m} value={m} />
              ))}
            </datalist>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>
              Resource <span className={styles.req}>*</span>
            </label>
            <input
              className={styles.input}
              value={form.resource}
              onChange={(e) => setField("resource", slug(e.target.value))}
              placeholder="invoice"
            />
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            Action <span className={styles.req}>*</span>
          </label>
          <div className={styles.actions}>
            {ACTIONS.map((a) => (
              <button
                key={a}
                type="button"
                className={`${styles.actionBtn} ${
                  form.action === a ? styles.actionActive : ""
                }`}
                onClick={() => setField("action", a)}
              >
                {a.charAt(0).toUpperCase() + a.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Description</label>
          <input
            className={styles.input}
            value={form.description}
            onChange={(e) => setField("description", e.target.value)}
            placeholder="Create invoices"
          />
        </div>

        <div className={styles.keyPreview}>
          <KeyRound size={14} />
          <span className={styles.keyLabel}>Generated key:</span>
          <code className={styles.keyCode}>{previewKey}</code>
        </div>
      </div>
    </BaseModal>
  );
};

export default AddPermissionModal;
